import React from "react";
import { Box, Container, styled, Button } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Styled components
const NavbarContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  borderBottom: "1px solid #e0e0e0",
  width: "100%",
  zIndex: theme.zIndex.appBar - 1,
}));

const DesktopNav = styled(Box)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("md")]: {
    display: "flex",
    height: "56px",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      right: 0,
      top: 0,
      height: "100%",
      width: "50px",
      background:
        "linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,1))",
      pointerEvents: "none",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      height: "100%",
      width: "50px",
      background:
        "linear-gradient(to left, rgba(255,255,255,0), rgba(255,255,255,1))",
      pointerEvents: "none",
      zIndex: 1,
    },
  },
}));

const NavItemsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "40px",
  padding: "0 40px",
  overflowX: "auto",
  width: "100%",
  scrollBehavior: "smooth",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  "-ms-overflow-style": "none",
  "scrollbar-width": "none",
}));

const NavItem = styled(Link)(({ theme, active }) => ({
  color: active ? theme.palette.primary.main : theme.palette.text.primary,
  textDecoration: "none",
  fontSize: "14px",
  fontWeight: 500,
  whiteSpace: "nowrap",
  padding: "18px 0",
  position: "relative",
  transition: "all 0.2s ease",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "2px",
    backgroundColor: theme.palette.primary.main,
    transform: active ? "scaleX(1)" : "scaleX(0)",
    transition: "transform 0.2s ease",
  },
  "&:hover": {
    color: theme.palette.primary.main,
    "&::after": {
      transform: "scaleX(1)",
    },
  },
}));

const MobileNav = styled(Box)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.down("md")]: {
    display: "flex",
    overflowX: "auto",
    gap: "8px",
    padding: "12px 20px",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
}));

const MobileNavButton = styled(Button)(({ theme, active }) => ({
  minWidth: "auto",
  padding: "6px 16px",
  borderRadius: "20px",
  fontSize: "14px",
  fontWeight: 500,
  textTransform: "none",
  whiteSpace: "nowrap",
  marginRight: "8px",
  border: `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
  color: active ? "#fff" : theme.palette.text.primary,
  backgroundColor: active ? theme.palette.primary.main : "#fff",
  "&:hover": {
    backgroundColor: active
      ? theme.palette.primary.dark
      : theme.palette.grey[100],
  },
}));

const Navbar = () => {
  const location = useLocation();
  const { t } = useTranslation();

  const navItems = [
    {
      label: t('navbar.home'),
      path: "/",
      matches: (path) => path === "/",
    },
    {
      label: t('navbar.exploreBooks'),
      path: "/books",
      matches: (path) => path.startsWith("/books"),
    },
    {
      label: t('navbar.audioBooks'),
      path: "/audio-books",
      matches: (path) => path.startsWith("/audio-books"),
    },
    {
      label: t('navbar.magazines'),
      path: "/magazines",
      matches: (path) => path.startsWith("/magazines"),
    },
    {
      label: t('navbar.freeBooks'),
      path: "/free-books",
      matches: (path) => path.startsWith("/free-books"),
    },
    {
      label: t('navbar.faqs'),
      path: "/help/faqs",
      matches: (path) => path.startsWith("/help/faqs"),
    },
    {
      label: t('navbar.aboutUs'),
      path: "/about",
      matches: (path) => path.startsWith("/about"),
    },
    {
      label: t('navbar.contactUs'),
      path: "/help/contact-us",
      matches: (path) => path.startsWith("/help/contact-us"),
    },
  ];

  // Helper function to check if a nav item is active
  const isActive = (item) => {
    return item.matches(location.pathname);
  };

  return (
    <NavbarContainer>
      <Container maxWidth="xl">
        <DesktopNav>
          <NavItemsContainer>
            {navItems.map((item, index) => (
              <NavItem key={index} to={item.path} active={isActive(item)}>
                {item.label}
              </NavItem>
            ))}
          </NavItemsContainer>
        </DesktopNav>

        <MobileNav>
          {navItems.map((item, index) => (
            <MobileNavButton
              key={index}
              component={Link}
              to={item.path}
              active={isActive(item)}
              variant="outlined"
            >
              {item.label}
            </MobileNavButton>
          ))}
        </MobileNav>
      </Container>
    </NavbarContainer>
  );
};

export default Navbar;
